if (!customElements.get('recently-viewed-products')) {
  const LOCAL_STORAGE_KEY = 'perfumania_recently_viewed';
  const MAX_PRODUCTS = 12;
  const memoryStore = {};

  const safeStorage = {
    getItem(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return memoryStore[key] || null;
      }
    },
    setItem(key, val) {
      try {
        localStorage.setItem(key, val);
      } catch (e) {
        memoryStore[key] = val;
      }
    },
  };

  class RecentlyViewedProducts extends HTMLElement {
    static getHandles(excludeHandle) {
      try {
        const stored = safeStorage.getItem(LOCAL_STORAGE_KEY);
        if (!stored) return [];
        const handles = JSON.parse(stored);
        if (!Array.isArray(handles)) return [];

        return handles.filter(
          (h) =>
            typeof h === 'string' &&
            h.trim().length > 0 &&
            /^[a-z0-9_\-]+$/i.test(h.trim()) &&
            h.trim() !== excludeHandle
        );
      } catch (e) {
        console.error('[RecentlyViewedProducts] Error reading storage data:', e);
        return [];
      }
    }

    static recordProduct(handle) {
      if (!handle || typeof handle !== 'string' || !handle.trim()) return;
      const trimmed = handle.trim();
      if (!/^[a-z0-9_\-]+$/i.test(trimmed)) return;

      try {
        let handles = RecentlyViewedProducts.getHandles();
        handles = handles.filter((h) => h !== trimmed);
        handles.unshift(trimmed);
        if (handles.length > MAX_PRODUCTS) {
          handles = handles.slice(0, MAX_PRODUCTS);
        }
        safeStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(handles));
      } catch (e) {
        console.error('[RecentlyViewedProducts] Error saving handle to storage:', e);
      }
    }

    static removeHandles(handlesToRemove) {
      if (!Array.isArray(handlesToRemove) || handlesToRemove.length === 0) return;
      try {
        const removeSet = new Set(handlesToRemove);
        let handles = RecentlyViewedProducts.getHandles();
        handles = handles.filter((h) => !removeSet.has(h));
        safeStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(handles));
      } catch (e) {
        console.error('[RecentlyViewedProducts] Error removing missing handles:', e);
      }
    }

    constructor() {
      super();
      this.isInitialized = false;
      this.abortController = null;
      this.requestId = 0;
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      const currentHandle = this.dataset.productHandle;
      this.initSection(currentHandle);
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    getHandles(excludeHandle) {
      return RecentlyViewedProducts.getHandles(excludeHandle);
    }

    recordProduct(handle) {
      RecentlyViewedProducts.recordProduct(handle);
    }

    removeHandles(handlesToRemove) {
      RecentlyViewedProducts.removeHandles(handlesToRemove);
    }

    initSection(currentHandle) {
      if (currentHandle && typeof currentHandle === 'string' && currentHandle.trim()) {
        this.recordProduct(currentHandle.trim());
      }

      const sectionId = this.dataset.sectionId;
      if (!sectionId || typeof sectionId !== 'string' || !sectionId.trim()) {
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('[RecentlyViewedProducts] Missing required data-section-id');
        }
        this.style.display = 'none';
        return;
      }

      const handles = this.getHandles(currentHandle ? currentHandle.trim() : null);
      if (!handles || handles.length === 0) {
        this.style.display = 'none';
        return;
      }

      const rawLimit = Number(this.dataset.limit);
      const limit =
        Number.isFinite(rawLimit) && rawLimit > 0
          ? Math.min(Math.floor(rawLimit), MAX_PRODUCTS)
          : Math.min(8, MAX_PRODUCTS);

      const targetHandles = handles.slice(0, limit);
      if (targetHandles.length === 0) {
        this.style.display = 'none';
        return;
      }

      const query = targetHandles.map((h) => `handle:${h}`).join(' OR ');
      const rootUrl =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
      const searchUrl = `${rootUrl}search?section_id=${encodeURIComponent(
        sectionId.trim()
      )}&type=product&q=${encodeURIComponent(query)}`;

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      const currentRequestId = ++this.requestId;

      fetch(searchUrl, { signal: this.abortController.signal })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status} ${res.statusText}`);
          }
          return res.text();
        })
        .then((html) => {
          if (currentRequestId !== this.requestId || !this.isConnected) return;

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newSection = doc.querySelector('recently-viewed-products');

          if (!newSection) {
            this.style.display = 'none';
            return;
          }

          const wrapper = newSection.querySelector('.swiper-wrapper');
          const slides = newSection.querySelectorAll('slideshow-container-item, .swiper-slide');

          if (!wrapper || slides.length === 0) {
            this.style.display = 'none';
            return;
          }

          // Re-order returned product elements to strictly match targetHandles order
          const slideMap = new Map();
          slides.forEach((slide) => {
            const handle = slide.dataset.productHandle;
            if (handle) {
              slideMap.set(handle, slide);
            }
          });

          if (slideMap.size > 0) {
            const orderedSlides = [];
            const returnedHandles = new Set();

            targetHandles.forEach((handle) => {
              const slide = slideMap.get(handle);
              if (slide) {
                orderedSlides.push(slide);
                returnedHandles.add(handle);
              }
            });

            if (orderedSlides.length > 0) {
              wrapper.innerHTML = '';
              orderedSlides.forEach((slide) => wrapper.appendChild(slide));
            }

            // Clean up missing/deleted product handles from storage
            const missingHandles = targetHandles.filter((h) => !returnedHandles.has(h));
            if (missingHandles.length > 0) {
              this.removeHandles(missingHandles);
            }
          }

          // Replacing innerHTML lets browser automatically disconnect old slideshow-container and connect the new one
          this.innerHTML = newSection.innerHTML;
          this.style.display = 'block';
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          this.style.display = 'none';
          console.error('[RecentlyViewedProducts] Error fetching section:', err);
        });
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);

  // Auto-record product handle on product pages (PDP) across the store
  const recordCurrentPageProduct = () => {
    try {
      let currentHandle = null;
      if (
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.handle
      ) {
        currentHandle = window.ShopifyAnalytics.meta.product.handle;
      } else if (window.location.pathname.includes('/products/')) {
        const parts = window.location.pathname.split('/products/');
        if (parts[1]) {
          currentHandle = parts[1].split('/')[0].split('?')[0];
        }
      }

      if (currentHandle && typeof currentHandle === 'string') {
        RecentlyViewedProducts.recordProduct(currentHandle);
      }
    } catch (e) {
      // Ignore
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recordCurrentPageProduct);
  } else {
    recordCurrentPageProduct();
  }
}
